// Firebase Cloud Messaging (FCM) service
// Flow:
//   1. registerServiceWorker()  — call on app mount, no user gesture needed
//   2. getFCMToken(swReg)       — call only after permission is 'granted'
//   3. onForegroundMessage()    — subscribe while tab is open
// Notification.requestPermission() is intentionally NOT called here —
// it must be triggered directly from a user click (browser restriction).

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getAnalytics, isSupported as isAnalyticsSupported, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import store from '@/store';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const isFirebaseConfigured = firebaseConfig.projectId && firebaseConfig.apiKey && firebaseConfig.appId;

const app = isFirebaseConfigured ? initializeApp(firebaseConfig) : null;
const messaging = isFirebaseConfigured ? getMessaging(app) : null;

// Google Analytics (GA4) via the Firebase SDK. Device, geography, session,
// traffic source, timestamp, and page-view context are all captured
// automatically by GA4 for every event — nothing to wire up manually for
// those. What this file adds is the ability to (a) log a specific
// click/action as a named event and (b) attribute events to a logged-in
// user via setUserId, once auth is known.
//
// isSupported() is genuinely async — GA4's SDK needs cookies/IndexedDB,
// which aren't available in every environment (e.g. some private-browsing
// modes) — so `analytics` starts null and is only set once the check
// resolves. logAnalyticsEvent/setAnalyticsUser silently no-op until then
// rather than queuing; that's fine since the events worth tracking
// precisely (a button click) happen well after page load.
// Some brand env files restrict analytics to one hostname (e.g. a CN staging
// Firebase project should only receive events from its real staging domain,
// not localhost/dev/other domains sharing this build). Unset = no restriction.
function isAnalyticsAllowedHost() {
  const allowedHost = import.meta.env.VITE_ANALYTICS_ALLOWED_HOST;
  return !allowedHost || window.location.hostname === allowedHost;
}

let analytics = null;
if (isFirebaseConfigured && firebaseConfig.measurementId && isAnalyticsAllowedHost()) {
  isAnalyticsSupported()
    .then((supported) => {
      if (supported) analytics = getAnalytics(app);
    })
    .catch(() => {
      // Unsupported environment — leave analytics null.
    });
}

// A second, standalone GA4 property — wired via the plain gtag.js snippet
// in index.html (its own script tag + `gtag('config', ...)` call) rather
// than the Firebase SDK, kept deliberately alongside the Firebase-linked
// property above. Both need every event for their own separate key-event/
// conversion tracking.
const SECONDARY_MEASUREMENT_ID = import.meta.env.VITE_GA_SECONDARY_MEASUREMENT_ID || '';

// `send_to` scopes this specific gtag() call to just that one property,
// rather than relying on gtag.js's default "broadcast to every configured
// id sharing this page's dataLayer" behavior — that default depends on
// script load order (whether Firebase's SDK ends up reusing the same
// dataLayer the index.html snippet created, or creating its own) and isn't
// something to build on deliberately.
// `gtag` is a global injected by index.html's plain <script> snippet, not a
// real DOM API — TypeScript's lib.dom has no idea it exists, hence the cast.
/** @returns {((...args: any[]) => void) | undefined} */
function getGtag() {
  return /** @type {any} */ (window).gtag;
}

function logToSecondaryProperty(eventName, params) {
  const gtag = getGtag();
  if (typeof gtag !== 'function' || !SECONDARY_MEASUREMENT_ID) return;
  gtag('event', eventName, { ...params, send_to: SECONDARY_MEASUREMENT_ID });
}

export function logAnalyticsEvent(eventName, params = {}) {
  if (analytics) logEvent(analytics, eventName, params);
  logToSecondaryProperty(eventName, params);
}

export function setAnalyticsUser(userId, properties = null) {
  if (analytics) {
    setUserId(analytics, userId || null);
    if (properties) setUserProperties(analytics, properties);
  }
  const gtag = getGtag();
  if (typeof gtag === 'function' && SECONDARY_MEASUREMENT_ID) {
    gtag('config', SECONDARY_MEASUREMENT_ID, { user_id: userId || undefined });
    if (properties) gtag('set', 'user_properties', properties);
  }
}

// Preferred entry point for feature code logging a custom event: re-applies
// the current logged-in user's GA4 User-ID first, so an event fired before
// (or without) an explicit setAnalyticsUser() call still gets attributed —
// e.g. analytics finishing its async isSupported() check after login, or a
// component that fires an event without going through the login flow itself.
export function trackAnalyticsEvent(eventName, params = {}) {
  const userId = store.getters.getUser?.id;
  if (userId) setAnalyticsUser(userId);
  logAnalyticsEvent(eventName, params);
}

// Registers firebase-messaging-sw.js at a narrow, dedicated scope — NOT root.
// Root scope is owned by public/sw.js (see registerMaintenanceServiceWorker
// below), whose only job is serving the maintenance-fallback page and must
// keep working even if this Firebase worker fails to install (e.g. its
// gstatic.com script imports get blocked). Scope doesn't need to correspond
// to a real route: push delivery and notificationclick handling
// (clients.matchAll({ includeUncontrolled: true })) both work regardless of
// which scope this registration "controls".
// Safe to call on mount — no user gesture needed.
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/firebase-cloud-messaging-push-scope/',
    });
    console.log('[FCM] SW registered:', reg.scope);
    return reg;
  } catch (err) {
    console.error('[FCM] SW registration failed:', err);
    return null;
  }
}

// Registers the maintenance-fallback worker (public/sw.js) at root scope.
// Independent of Firebase entirely — see that file for why. Safe to call
// unconditionally for every visitor, authenticated or not.
export async function registerMaintenanceServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (err) {
    console.error('[Maintenance SW] registration failed:', err);
    return null;
  }
}

// Retrieves the FCM device token. Call only after permission is already 'granted'.
/** @param {ServiceWorkerRegistration} swRegistration */
export async function getFCMToken(swRegistration) {
  if (!messaging) return null;
  try {
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });
    if (token) {
      console.log('[FCM] Token:', token);
    } else {
      console.warn('[FCM] No token — check VAPID key');
    }
    return token || null;
  } catch (err) {
    console.error('[FCM] getToken failed:', err);
    return null;
  }
}

export function onForegroundMessage(callback) {
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}
